import { QuestionCircleOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, DatePicker, Input, Select, Space, Spin, Tooltip } from 'antd'
import cx from 'classnames'
import dayjs from 'dayjs'

import type { CAEpayGroup, FiltersState } from '../types'

interface FiltersProps {
  filters: FiltersState
  groups?: CAEpayGroup[]
  groupsLoading: boolean
  isRefreshing: boolean
  groupSearchValue: string
  adjustedFromDate: string | null
  onGroupSearch: (value: string) => void
  onGroupChange: (value?: string) => void
  onDateFromChange: (value?: string) => void
  onDateToChange: (value?: string) => void
  onCheckNumberChange: (value?: string) => void
  onRefresh: () => void
}

export function Filters({
  filters,
  groups,
  groupsLoading,
  isRefreshing,
  groupSearchValue,
  adjustedFromDate,
  onGroupSearch,
  onGroupChange,
  onDateFromChange,
  onDateToChange,
  onCheckNumberChange,
  onRefresh
}: FiltersProps): React.ReactNode {
  return (
    <div className='flex flex-wrap gap-4 items-end p-4 border-b'>
      <div className='flex flex-col gap-1'>
        <label className='flex items-center gap-1 text-xs font-medium text-gray-500 uppercase'>
          Group
          <Tooltip title='Select a California group to filter check registers. Required to activate the sliding date parameter.'>
            <QuestionCircleOutlined className='text-gray-400 cursor-help' />
          </Tooltip>
        </label>
        <Select
          placeholder='Search and select group'
          className='w-[500px]'
          loading={groupsLoading}
          value={filters.groupNumber ?? null}
          onChange={value => onGroupChange(value ?? undefined)}
          showSearch
          filterOption={false}
          searchValue={groupSearchValue}
          onSearch={onGroupSearch}
          allowClear
          notFoundContent={
            groupsLoading ? (
              <div className='flex justify-center py-2'>
                <Spin size='small' />
              </div>
            ) : (
              'No groups found'
            )
          }
          options={groups?.map(g => ({
            value: g.number,
            label: g.name ? `${g.number} - ${g.name}` : g.number
          }))}
        />
      </div>

      <div className='flex flex-col gap-1'>
        <label className='flex items-center gap-1 text-xs font-medium text-gray-500 uppercase'>
          From
          <Tooltip
            title={
              <span>
                Start of check date range <em>(mkckdt)</em>. When a group is selected and left
                blank, this is auto-calculated as the day after the last processed void date{' '}
                <em>(mkcvdt)</em> — the sliding parameter. Set manually to override.
              </span>
            }
          >
            <QuestionCircleOutlined className='text-gray-400 cursor-help' />
          </Tooltip>
        </label>
        <div className='flex flex-col gap-0.5'>
          <DatePicker
            value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
            onChange={date => onDateFromChange(date?.format('YYYY-MM-DD'))}
            placeholder={
              adjustedFromDate && filters.groupNumber
                ? `Auto: ${dayjs(adjustedFromDate).format('MM/DD/YYYY')}`
                : 'Auto'
            }
            allowClear
          />
          {!filters.dateFrom && adjustedFromDate && filters.groupNumber && (
            <span className='text-[11px] text-blue-500 leading-none'>
              Auto: {dayjs(adjustedFromDate).format('MM/DD/YYYY')} (last void + 1 day)
            </span>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-1'>
        <label className='flex items-center gap-1 text-xs font-medium text-gray-500 uppercase'>
          To
          <Tooltip title='End of check date range. Filters by check date (mkckdt).'>
            <QuestionCircleOutlined className='text-gray-400 cursor-help' />
          </Tooltip>
        </label>
        <DatePicker
          value={filters.dateTo ? dayjs(filters.dateTo) : null}
          onChange={date => onDateToChange(date?.format('YYYY-MM-DD'))}
          allowClear
        />
      </div>

      <div className='flex flex-col gap-1'>
        <label className='flex items-center gap-1 text-xs font-medium text-gray-500 uppercase'>
          Check #
          <Tooltip title='Search by full or partial check number.'>
            <QuestionCircleOutlined className='text-gray-400 cursor-help' />
          </Tooltip>
        </label>
        <Input
          placeholder='Search check number'
          className='w-40'
          prefix={<SearchOutlined className='text-gray-400' />}
          value={filters.checkNumber}
          onChange={e => onCheckNumberChange(e.target.value || undefined)}
          allowClear
        />
      </div>

      <Space>
        <Button
          type='default'
          disabled={isRefreshing}
          onClick={onRefresh}
          icon={<SyncOutlined className={cx(isRefreshing && 'animate-custom-spin')} />}
        >
          Refresh
        </Button>
      </Space>
    </div>
  )
}
